import { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';
import { Power, AlertTriangle, Settings, Play, Square, RefreshCw, X } from 'lucide-react';

interface SystemSettingsData {
  postingEnabled: boolean;
  emailCampaignsEnabled: boolean;
  contentGenerationEnabled: boolean;
  emergencyStop: boolean;
  maintenanceMode: boolean;
  emergencyStopReason?: string;
  emergencyStopTimestamp?: string;
  maxPostsPerDay: number;
  allowedPlatforms: string[];
  postingSchedule: { startHour: number; endHour: number; timezone: string };
  maxEmailsPerDay: number;
  emailSendingEnabled: boolean;
  maxContentGenerationsPerDay: number;
  allowedContentTypes: string[];
  emergencyContactEmail: string;
  createdAt: string;
  updatedAt: string;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-primary' : 'bg-white/[0.15]'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function SystemSettings() {
  const [settings, setSettings] = useState<SystemSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState("");
  const [emergencyEmail, setEmergencyEmail] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch("/api/admin/system-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        setEmergencyEmail(data.settings.emergencyContactEmail || "");
        setError("");
      } else {
        setError(`Failed to load system settings: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      setError(`Failed to load system settings: ${err instanceof Error ? err.message : "Network error"}`);
    } finally {
      setLoading(false);
    }
  };

  const updateKillSwitches = async (updates: {
    postingEnabled?: boolean;
    emailCampaignsEnabled?: boolean;
    contentGenerationEnabled?: boolean;
  }) => {
    try {
      setSaving(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch("/api/admin/system-settings/kill-switches", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        const data = await response.json();
        setSettings((prev) => (prev ? { ...prev, ...data.settings } : null));
        setSuccess("Kill switches updated successfully");
      } else {
        setError("Failed to update kill switches");
      }
    } catch { setError("Failed to update kill switches"); }
    finally { setSaving(false); }
  };

  const handleEmergencyStop = async () => {
    if (!emergencyReason.trim()) { setError("Emergency stop reason is required"); return; }
    try {
      setSaving(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch("/api/admin/system-settings/emergency-stop", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: emergencyReason, contactEmail: emergencyEmail }),
      });
      if (response.ok) {
        setEmergencyDialogOpen(false);
        setEmergencyReason("");
        await fetchSettings();
        setSuccess("Emergency stop activated — all operations halted");
      } else { setError("Failed to activate emergency stop"); }
    } catch { setError("Failed to activate emergency stop"); }
    finally { setSaving(false); }
  };

  const handleResumeOperations = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch("/api/admin/system-settings/resume-operations", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) { await fetchSettings(); setSuccess("Operations resumed successfully"); }
      else { setError("Failed to resume operations"); }
    } catch { setError("Failed to resume operations"); }
    finally { setSaving(false); }
  };

  const getStatusText = () => {
    if (!settings) return "Unknown";
    if (settings.emergencyStop) return "Emergency Stop";
    if (settings.maintenanceMode) return "Maintenance Mode";
    return "Operational";
  };

  const getStatusCls = () => {
    if (!settings) return "bg-white/10 text-white/40";
    if (settings.emergencyStop) return "bg-red/15 text-red border border-red/20";
    if (settings.maintenanceMode) return "bg-orange/15 text-orange border border-orange/20";
    return "bg-green/15 text-green border border-green/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-16">
        <p className="text-red text-sm">Failed to load system settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">System Settings & Kill Switches</h1>
        <button onClick={fetchSettings} disabled={loading} className={BTN_SECONDARY}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm">{success}</div>}

      {/* System Status */}
      <div className={`${CARD} p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
            <Power size={16} className="text-primary" />
          </div>
          <h2 className="text-sm font-semibold text-white">System Status</h2>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${getStatusCls()}`}>
            {settings.emergencyStop && <AlertTriangle size={11} />}
            {getStatusText()}
          </span>
          <span className="text-white/40 text-xs">Last updated: {new Date(settings.updatedAt).toLocaleString()}</span>
        </div>

        {settings.emergencyStop && (
          <div className="bg-red/10 border border-red/20 rounded-lg p-4 mb-4 space-y-1">
            <p className="text-red text-sm font-medium">Emergency Stop Active: {settings.emergencyStopReason}</p>
            <p className="text-red/70 text-xs">Activated: {settings.emergencyStopTimestamp ? new Date(settings.emergencyStopTimestamp).toLocaleString() : "Unknown"}</p>
            <p className="text-red/70 text-xs">Contact: {settings.emergencyContactEmail}</p>
          </div>
        )}

        <div className="flex gap-2">
          {!settings.emergencyStop ? (
            <button onClick={() => setEmergencyDialogOpen(true)} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-red/15 hover:bg-red/25 text-red border border-red/20 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50">
              <Square size={14} /> Emergency Stop
            </button>
          ) : (
            <button onClick={handleResumeOperations} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-green/15 hover:bg-green/25 text-green border border-green/20 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50">
              <Play size={14} /> Resume Operations
            </button>
          )}
        </div>
      </div>

      {/* Kill Switches */}
      <div className={`${CARD} p-5`}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-red/15 flex items-center justify-center">
            <Power size={16} className="text-red" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">Kill Switches</h2>
            <p className="text-white/30 text-xs">Control major system operations</p>
          </div>
        </div>

        <div className="space-y-1">
          {([
            { label: 'Social Media Posting', desc: 'Enable/disable automated posting to social platforms', key: 'postingEnabled' as const, value: settings.postingEnabled },
            { label: 'Email Campaigns', desc: 'Enable/disable automated email sending', key: 'emailCampaignsEnabled' as const, value: settings.emailCampaignsEnabled },
            { label: 'Content Generation', desc: 'Enable/disable AI content creation', key: 'contentGenerationEnabled' as const, value: settings.contentGenerationEnabled },
          ] as const).map(({ label, desc, key, value }) => (
            <div key={key} className="flex items-center justify-between gap-4 py-4 border-b border-white/[0.04] last:border-0">
              <div>
                <p className="text-sm font-medium text-white/80">{label}</p>
                <p className="text-white/40 text-xs mt-0.5">{desc}</p>
              </div>
              <Toggle
                checked={value}
                onChange={(v) => updateKillSwitches({ [key]: v })}
                disabled={saving || settings.emergencyStop}
              />
            </div>
          ))}
        </div>
      </div>

      {/* System Limits */}
      <div className={`${CARD} p-5`}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center">
            <Settings size={16} className="text-white/50" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">System Limits</h2>
            <p className="text-white/30 text-xs">Current operational limits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          {[
            { label: 'Daily Post Limit', value: settings.maxPostsPerDay, unit: 'posts per day' },
            { label: 'Daily Email Limit', value: settings.maxEmailsPerDay, unit: 'emails per day' },
            { label: 'Content Generation Limit', value: settings.maxContentGenerationsPerDay, unit: 'generations per day' },
          ].map((item) => (
            <div key={item.label} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-4">
              <p className="text-white/40 text-xs mb-1">{item.label}</p>
              <p className="text-3xl font-bold text-primary">{item.value}</p>
              <p className="text-white/30 text-xs mt-1">{item.unit}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-white/[0.06] pt-4">
          <p className="text-white/40 text-xs mb-1 font-medium uppercase tracking-wider">Posting Schedule</p>
          <p className="text-white/70 text-sm">
            {settings.postingSchedule.startHour}:00 – {settings.postingSchedule.endHour}:00
            <span className="text-white/40 ml-2">({settings.postingSchedule.timezone})</span>
          </p>
        </div>
      </div>

      {/* Emergency Stop Dialog */}
      <Dialog.Root open={emergencyDialogOpen} onOpenChange={(v) => !v && setEmergencyDialogOpen(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-[#111113] border border-red/20 rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-red flex items-center gap-2">
                <AlertTriangle size={16} /> 🚨 Emergency Stop
              </Dialog.Title>
              <Dialog.Close className="text-white/40 hover:text-white transition-colors"><X size={18} /></Dialog.Close>
            </div>

            <div className="bg-red/10 border border-red/20 rounded-lg p-4 mb-5">
              <p className="text-white/70 text-sm mb-2">This will immediately halt ALL system operations including:</p>
              <ul className="text-red/80 text-sm space-y-0.5 list-disc list-inside">
                <li>Social media posting</li>
                <li>Email campaigns</li>
                <li>Content generation</li>
                <li>Scheduled jobs</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Reason for Emergency Stop *</label>
                <textarea
                  className={INPUT}
                  value={emergencyReason}
                  onChange={(e) => setEmergencyReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why you're activating emergency stop..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Contact Email</label>
                <input
                  type="email"
                  className={INPUT}
                  value={emergencyEmail}
                  onChange={(e) => setEmergencyEmail(e.target.value)}
                  placeholder="admin@yourcompany.com"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/[0.06]">
              <button onClick={() => setEmergencyDialogOpen(false)} disabled={saving} className={BTN_SECONDARY}>Cancel</button>
              <button
                onClick={handleEmergencyStop}
                disabled={saving || !emergencyReason.trim()}
                className="inline-flex items-center gap-1.5 bg-red hover:bg-red text-white rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> Activating…</>
                  : <><Square size={14} /> Emergency Stop</>}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default SystemSettings;
